import { House, List, MagnifyingGlass, MapPin, Plus, type Icon } from '@phosphor-icons/react'
import type { CSSProperties } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { cn } from '@/design-system'
import { useT } from '@/i18n'
import { useCan } from '@/stores/auth'
import { useUi } from '@/stores/ui'
import { useTabLink } from './workspace/tabMeta'

const HOME = '/'
const SHIPMENTS = '/reports/shipment-details'
const TRACKING = '/reports/awb-tracking'

/**
 * The phone's bottom bar (owner, 2026-10-30: «Floating Pill Bottom Navigation with Animated Active Tab»): a floating
 * light pill with four places — home, shipments, tracking, the menu — and the create button in its middle.
 * The active place sits on a see-through violet tile that slides from one to the next inside its own slot.
 * Hidden from md up (the sidebar is there); respects the home-indicator safe area.
 */
export function MobileTabBar() {
  const { locale } = useT()
  const ar = locale === 'ar'
  const { pathname } = useLocation()
  const menuOpen = useUi((s) => s.mobileNavOpen)
  const setMobileNav = useUi((s) => s.setMobileNav)
  const canCreate = useCan('shipments:write')
  // each shortcut opens (or brings back) its own tab instead of replacing the current page
  const tabLink = useTabLink()

  // which of the four the sliding pill sits under (none on any other page); home may land on «صفحتي» (/my)
  const active = menuOpen ? 3 : pathname === HOME || pathname === '/my' ? 0 : pathname.startsWith(SHIPMENTS) ? 1 : pathname.startsWith(TRACKING) ? 2 : -1

  const face = (I: Icon, label: string, on: boolean) => (
    <>
      <I size={21} weight={on ? 'fill' : 'regular'} className="shrink-0" />
      <span className="pill-label">{label}</span>
    </>
  )
  const tab = (i: number, to: string, I: Icon, label: string) => (
    <NavLink to={to} end={to === HOME} {...tabLink(to)} className={cn('pill-tab', active === i && 'is-on')} aria-label={label}>
      {face(I, label, active === i)}
    </NavLink>
  )

  // the pill's slot: the create button sits in the middle, so tracking and the menu are one slot further on
  const slot = Math.max(active, 0)
  return (
    <nav className="pillbar md:hidden" aria-label={ar ? 'التنقل السريع' : 'Quick navigation'}>
      <div className={cn('pillbar-track', canCreate && 'has-create')} style={{ '--i': slot, '--past': canCreate && slot >= 2 ? 1 : 0 } as CSSProperties}>
        <span aria-hidden className={cn('pillbar-glow', active < 0 && 'is-off')} />
        {tab(0, HOME, House, ar ? 'الرئيسية' : 'Home')}
        {tab(1, SHIPMENTS, MagnifyingGlass, ar ? 'الشحنات' : 'Shipments')}
        {canCreate && (
          <NavLink to="/operations/shipments/new" {...tabLink('/operations/shipments/new')} className="pillbar-create" aria-label={ar ? 'شحنة جديدة' : 'New shipment'}>
            <span><Plus size={24} weight="bold" /></span>
          </NavLink>
        )}
        {tab(2, TRACKING, MapPin, ar ? 'التتبع' : 'Tracking')}
        <button type="button" className={cn('pill-tab', active === 3 && 'is-on')} onClick={() => setMobileNav(true)} aria-label={ar ? 'القائمة' : 'Menu'}>
          {face(List, ar ? 'القائمة' : 'Menu', active === 3)}
        </button>
      </div>
    </nav>
  )
}
